import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ChevronLeft, ListMusic, Youtube } from 'lucide-react';
import PublicLayout from '../../components/public/PublicLayout';
import SEO from '../../components/SEO';
import ShareButtons from '../../components/ShareButtons';
import ContentCommentSection from '../../components/public/ContentCommentSection';
import { useMusicPlayer } from '../../contexts/MusicPlayerContext';

const FALLBACK_IMG =
  'https://distribution-pink.s3.ap-northeast-3.amazonaws.com/dev/images/none_image.jpg';

const formatDuration = (sec) => {
  if (!sec) return '';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

function YoutubePlayer({ videoId, playlistName, coverImage, motionLoopUrl, onPlayOnSite, playableCount }) {
  const iframeRef = useRef(null);
  const [ytError, setYtError] = useState(false);
  const [errorCode, setErrorCode] = useState(null);

  useEffect(() => {
    setYtError(false);
    setErrorCode(null);

    // enablejsapi 이벤트는 부모가 'listening' 핸드셰이크를 보내기 전까지 오지 않는다.
    // 이게 빠지면 임베드 차단(오류 101/150)을 수신하지 못해 유튜브 기본 차단 화면이 그대로 노출됨.
    let gotMessage = false;
    const sendListening = () => {
      try {
        iframeRef.current?.contentWindow?.postMessage(
          JSON.stringify({ event: 'listening', id: 'pinkyt', channel: 'widget' }),
          'https://www.youtube.com'
        );
      } catch (_) {}
    };
    const handshake = setInterval(() => {
      if (gotMessage) return clearInterval(handshake);
      sendListening();
    }, 400);
    const handshakeStop = setTimeout(() => clearInterval(handshake), 15000);

    const handleMessage = (event) => {
      if (event.origin !== 'https://www.youtube.com') return;
      gotMessage = true;
      try {
        const data = JSON.parse(event.data);
        if (data.event === 'onError') {
          console.error('[YouTube Embed] 에러 코드:', data.info);
          setErrorCode(data.info);
          setYtError(true);
        }
      } catch (_) {}
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
      clearInterval(handshake);
      clearTimeout(handshakeStop);
    };
  }, [videoId]);

  if (ytError) {
    return (
      <div className="relative w-full rounded-2xl overflow-hidden" style={{ paddingTop: '56.25%' }}>
        {motionLoopUrl ? (
          <video src={motionLoopUrl} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" />
        ) : coverImage ? (
          <img src={coverImage} alt={playlistName} className="absolute inset-0 w-full h-full object-cover" />
        ) : null}
        <div className="absolute inset-0 bg-slate-950/60 flex flex-col items-center justify-center gap-3 px-6 text-center">
          <p className="text-white text-sm font-medium">일부 곡의 저작권 정책으로 이 영상은 웹 페이지 안에서 재생할 수 없어요</p>
          <div className="flex flex-wrap items-center justify-center gap-2 mt-1">
            {onPlayOnSite && playableCount > 0 && (
              <button
                onClick={onPlayOnSite}
                className="flex items-center gap-2 bg-sky-500 hover:bg-sky-400 text-white text-sm font-semibold px-5 py-2 rounded-full transition-colors">
                ▶ 사이트 플레이어로 듣기 ({playableCount}곡)
              </button>
            )}
            <a
              href={`https://www.youtube.com/watch?v=${videoId}`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 bg-red-600 hover:bg-red-500 text-white text-sm font-medium px-5 py-2 rounded-full transition-colors">
              <Youtube size={16} />
              YouTube에서 재생
            </a>
          </div>
        </div>
      </div>
    );
  }

  const isPlaylist = videoId.startsWith('PL');
  const origin = encodeURIComponent(window.location.origin);
  const embedSrc = isPlaylist
    ? `https://www.youtube.com/embed/videoseries?list=${videoId}&enablejsapi=1&rel=0&modestbranding=1&origin=${origin}`
    : `https://www.youtube.com/embed/${videoId}?enablejsapi=1&rel=0&modestbranding=1&origin=${origin}`;

  return (
    <div className="relative w-full rounded-2xl overflow-hidden" style={{ paddingTop: '56.25%' }}>
      <iframe
        ref={iframeRef}
        className="absolute inset-0 w-full h-full"
        src={embedSrc}
        title={playlistName}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
      />
    </div>
  );
}

function PublicPlaylistDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addAndPlay, addBulkToPlaylist } = useMusicPlayer();

  const [playlist, setPlaylist] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get('/public/playlists', { params: { offset: 0, limit: 100 } });
        const found = (res.data.items || []).find((p) => String(p.id) === String(id));
        setPlaylist(found || null);
      } catch (err) {
        console.error('플레이리스트 로드 실패:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [id]);

  const songs = playlist?.songs || [];
  const totalDuration = songs.reduce((sum, s) => sum + (s.duration || 0), 0);

  if (loading) {
    return (
      <PublicLayout>
        <div className="flex items-center justify-center h-[60vh] gap-3 text-slate-400">
          <Loader2 size={22} className="animate-spin" />
          <span>로딩 중...</span>
        </div>
      </PublicLayout>
    );
  }

  if (!playlist) {
    return (
      <PublicLayout>
        <div className="flex items-center justify-center h-[60vh] text-slate-400">
          플레이리스트를 찾을 수 없습니다.
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <SEO
        title={playlist.name || '플레이리스트'}
        description={playlist.description || 'AI가 큐레이션한 플레이리스트'}
        path={`/playlists/${id}`}
        image={playlist.coverImage}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'MusicPlaylist',
          name: playlist.name,
          description: playlist.description,
          image: playlist.coverImage,
          url: `https://impink.co.kr/playlists/${id}`,
          numTracks: playlist.songs?.length || 0,
        }}
      />
      {/* 헤더 */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center scale-110"
          style={{ backgroundImage: `url(${playlist.coverImage || FALLBACK_IMG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0d1526] via-[#0d1526]/80 to-[#0d1526]/40" />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20 flex items-end gap-8">
          <img
            src={playlist.coverImage || FALLBACK_IMG}
            alt={playlist.name}
            className="hidden sm:block w-36 h-36 md:w-48 md:h-48 rounded-2xl object-cover shadow-2xl shrink-0"
          />

          <div className="min-w-0">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-1 text-slate-400 hover:text-white text-sm mb-3 transition-colors">
              <ChevronLeft size={15} />
              뒤로
            </button>
            <span className="inline-block text-xs text-sky-400 bg-sky-500/10 px-2 py-0.5 rounded-full mb-2">
              {playlist.concept}
            </span>
            <h1 className="text-white text-3xl md:text-4xl font-bold mb-1 leading-tight">
              {playlist.name}
            </h1>
            <p className="text-slate-300 text-sm mb-1 line-clamp-2">{playlist.description}</p>
            <div className="flex items-center gap-3 text-slate-400 text-sm mb-5">
              <span>
                {songs.length}곡
                {totalDuration > 0 && ` · ${
                  totalDuration >= 3600
                    ? `${Math.floor(totalDuration / 3600)}시간 ${Math.round((totalDuration % 3600) / 60)}분`
                    : `${Math.round(totalDuration / 60)}분`
                }`}
              </span>
              <ShareButtons
                title={playlist.name}
                description={playlist.description}
                image={playlist.coverImage}
              />
            </div>

          </div>
        </div>
      </section>

      {/* YouTube 영상 */}
      {playlist.youtubeVideoId && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-6">
          <YoutubePlayer
            videoId={playlist.youtubeVideoId}
            playlistName={playlist.name}
            coverImage={playlist.coverImage}
            motionLoopUrl={playlist.motionLoopUrl}
            playableCount={songs.filter((s) => s.fileUrl || s.youtubeVideoId).length}
            onPlayOnSite={() => {
              const playable = songs.filter((s) => s.fileUrl || s.youtubeVideoId);
              if (playable.length === 0) return;
              addAndPlay(playable[0]);
              if (playable.length > 1) addBulkToPlaylist(playable.slice(1));
            }}
          />
        </section>
      )}

      {/* 수록곡 목록 */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {songs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3 text-slate-500">
            <ListMusic size={40} />
            <p>수록곡이 없습니다.</p>
          </div>
        ) : (
          <div className="space-y-0.5">
            {songs.map((song, idx) => (
              <div
                key={`${song.id}-${idx}`}
                className="flex items-center gap-3 px-3 py-3 rounded-xl">
                {/* 트랙 번호 */}
                <div className="w-7 text-center shrink-0">
                  <span className="text-slate-500 text-sm">{idx + 1}</span>
                </div>

                {/* 앨범아트 */}
                <img
                  src={song.albumImage || FALLBACK_IMG}
                  alt=""
                  className="w-10 h-10 rounded-md object-cover shrink-0"
                />

                {/* 곡명 */}
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium truncate block text-white">
                    {song.nameKr}
                  </span>
                  <span className="sm:hidden text-xs text-slate-500 truncate block mt-0.5">
                    {song.artistKr}
                  </span>
                </div>

                {/* 아티스트명 (PC) */}
                <span className="hidden sm:block text-xs text-slate-500 truncate w-32 md:w-44 shrink-0 text-right">
                  {song.artistKr}
                </span>

                {/* 러닝타임 */}
                {song.duration > 0 && (
                  <span className="text-xs text-slate-600 w-10 text-right shrink-0 tabular-nums">
                    {formatDuration(song.duration)}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-12">
        <ContentCommentSection targetType="playlist" targetId={id} />
      </div>
    </PublicLayout>
  );
}

export default PublicPlaylistDetailPage;
